import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { firebaseEnabled, getFirebaseAuth, initializeFirebaseAuth } from '../lib/firebase';

export interface AccountUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
}

interface AuthContextValue {
  enabled: boolean;
  loading: boolean;
  user: AccountUser | null;
  error: string | null;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
  sendVerification: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<AccountUser | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const toAccountUser = (user: User | null): AccountUser | null =>
  user
    ? {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
      }
    : null;

const actionUrl = (): string => `${window.location.origin}/account`;

export const readableAuthError = (error: unknown, isChinese = false): string => {
  const code = typeof error === 'string'
    ? error
    : typeof error === 'object' && error && 'code' in error
      ? String((error as { code?: string }).code)
      : '';
  const chinese = {
    'auth/email-already-in-use': '这个邮箱已注册。请直接登录或重置密码。',
    'auth/invalid-credential': '邮箱或密码不正确。',
    'auth/invalid-email': '请输入有效的邮箱地址。',
    'auth/weak-password': '请使用至少 8 位的密码。',
    'auth/popup-closed-by-user': '登录窗口已关闭。请再次尝试。',
    'auth/popup-blocked': '浏览器阻止了登录窗口。请允许弹窗后重试。',
    'auth/unauthorized-domain': '此网站域名尚未获 Firebase 授权。请联系网站管理员完成配置。',
    'auth/operation-not-allowed': 'Google 登录尚未在 Firebase 中启用。请联系网站管理员完成配置。',
    'auth/redirect-cancelled-by-user': 'Google 登录已取消。请再次尝试。',
    'auth/redirect-operation-pending': '已有一个 Google 登录正在进行。请稍候或刷新页面后重试。',
    'auth/web-storage-unsupported': '当前浏览器阻止了登录所需的存储。请用 Safari、Chrome 或 Edge 打开网站后重试。',
    'auth/operation-not-supported-in-this-environment': '当前内嵌浏览器不支持 Google 登录。请在 Safari、Chrome 或 Edge 中直接打开本站后重试。',
    'auth/network-request-failed': '浏览器无法连接 Firebase 登录服务。请检查网络、关闭内容拦截器，或用 Safari、Chrome 或 Edge 重试。',
    'auth/invalid-api-key': 'Firebase 登录配置无效。请联系网站管理员。',
    'auth/app-not-authorized': '此应用尚未获 Firebase 授权。请联系网站管理员。',
    'auth/unauthorized-continue-uri': '登录完成后的返回地址未获授权。请联系网站管理员。',
    'auth/too-many-requests': '尝试次数过多，请稍后再试。',
  } as Record<string, string>;
  const english = {
    'auth/email-already-in-use': 'This email already has an account. Sign in or reset your password instead.',
    'auth/invalid-credential': 'The email or password is not correct.',
    'auth/invalid-email': 'Enter a valid email address.',
    'auth/weak-password': 'Use a password with at least 8 characters.',
    'auth/popup-closed-by-user': 'The sign-in window was closed. Please try again.',
    'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups and try again.',
    'auth/unauthorized-domain': 'This site domain is not yet authorized for Firebase sign-in. Please contact the site owner.',
    'auth/operation-not-allowed': 'Google sign-in is not enabled in Firebase yet. Please contact the site owner.',
    'auth/redirect-cancelled-by-user': 'Google sign-in was cancelled. Please try again.',
    'auth/redirect-operation-pending': 'A Google sign-in is already in progress. Please wait or refresh the page and try again.',
    'auth/web-storage-unsupported': 'This browser blocks the storage required for sign-in. Please retry in Safari, Chrome, or Edge.',
    'auth/operation-not-supported-in-this-environment': 'This embedded browser does not support Google sign-in. Open this site directly in Safari, Chrome, or Edge and try again.',
    'auth/network-request-failed': 'This browser could not reach Firebase sign-in. Check your connection, disable content blockers, or retry in Safari, Chrome, or Edge.',
    'auth/invalid-api-key': 'The Firebase sign-in configuration is invalid. Please contact the site owner.',
    'auth/app-not-authorized': 'This application is not authorized for Firebase sign-in. Please contact the site owner.',
    'auth/unauthorized-continue-uri': 'The post-sign-in return address is not authorized. Please contact the site owner.',
    'auth/too-many-requests': 'Too many attempts. Please wait and try again.',
  } as Record<string, string>;
  const fallback = isChinese ? '暂时无法完成登录，请稍后重试。' : 'We could not complete sign-in. Please try again shortly.';
  return (isChinese ? chinese : english)[code] || fallback;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AccountUser | null>(null);
  const [loading, setLoading] = useState(firebaseEnabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!firebaseEnabled) {
      setLoading(false);
      return undefined;
    }

    let unsubscribe: (() => void) | undefined;
    let active = true;
    void initializeFirebaseAuth()
      .then((auth) => {
        if (!auth || !active) return;
        unsubscribe = onAuthStateChanged(auth, (nextUser) => {
          if (!active) return;
          setUser(toAccountUser(nextUser));
          setLoading(false);
        });
      })
      .catch(() => {
        if (!active) return;
        setError('auth/internal-error');
        setLoading(false);
      });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const withAuth = async <T,>(operation: (auth: NonNullable<ReturnType<typeof getFirebaseAuth>>) => Promise<T>): Promise<T> => {
    const auth = await initializeFirebaseAuth();
    if (!auth) throw new Error('Firebase is not configured.');
    setError(null);
    try {
      return await operation(auth);
    } catch (nextError) {
      const errorCode = typeof nextError === 'object' && nextError && 'code' in nextError
        ? String((nextError as { code?: string }).code)
        : 'auth/internal-error';
      setError(errorCode);
      throw nextError;
    }
  };

  const value = useMemo<AuthContextValue>(() => ({
    enabled: firebaseEnabled,
    loading,
    user,
    error,
    signInWithEmail: async (email, password) => {
      await withAuth(async (auth) => {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      });
    },
    signUpWithEmail: async (email, password) => {
      await withAuth(async (auth) => {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        await sendEmailVerification(credential.user, { url: actionUrl() });
      });
    },
    signInWithGoogle: async () => {
      await withAuth(async (auth) => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        // This app is hosted outside Firebase Hosting. Popup sign-in avoids the
        // cross-origin iframe storage that redirect sign-in needs and that
        // current browsers commonly block on third-party domains.
        await signInWithPopup(auth, provider);
      });
    },
    signOutUser: async () => {
      await withAuth(async (auth) => signOut(auth));
    },
    sendVerification: async () => {
      await withAuth(async (auth) => {
        if (!auth.currentUser) throw new Error('No current user.');
        await sendEmailVerification(auth.currentUser, { url: actionUrl() });
      });
    },
    resetPassword: async (email) => {
      await withAuth(async (auth) => {
        await sendPasswordResetEmail(auth, email.trim(), { url: actionUrl() });
      });
    },
    refreshUser: async () => {
      const auth = await initializeFirebaseAuth();
      if (!auth?.currentUser) return null;
      await reload(auth.currentUser);
      const nextUser = toAccountUser(auth.currentUser);
      setUser(nextUser);
      return nextUser;
    },
  }), [error, loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
};
